import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { api, CATEGORIES, SUBCATEGORIES } from "../lib/api";
import PlaceCard from "../components/PlaceCard";

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [places, setPlaces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "all";
  const subcategory = params.get("subcategory") || "all";
  const city = params.get("city") || "all";
  const q = params.get("q") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v); else next.delete(k);
    if (k === "category") next.delete("subcategory");
    setParams(next);
  };

  useEffect(() => { api.get("/places/cities").then(r => setCities(r.data)).catch(()=>{}); }, []);

  useEffect(() => {
    setLoading(true);
    const reqParams = {};
    if (category !== "all") reqParams.category = category;
    if (subcategory !== "all") reqParams.subcategory = subcategory;
    if (city !== "all") reqParams.city = city;
    if (q) reqParams.q = q;
    api.get("/places", { params: reqParams }).then(r => setPlaces(r.data)).finally(()=>setLoading(false));
  }, [category, subcategory, city, q]);

  const subList = SUBCATEGORIES[category] || [];

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="explore-page">
      <div className="pt-24 sm:pt-28 pb-16 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-6">
          <div className="uppercase-label text-[#FF6B35]">Explorer</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[#1A1A1A] mt-1">
            Tous les spots de l'île
          </h1>
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1A1A1A]/50" />
          <Input
            data-testid="explore-search"
            value={q}
            onChange={(e) => setParam("q", e.target.value)}
            placeholder="Cherche un lieu, un plat, un quartier…"
            className="pl-10 rounded-full bg-white border-black/10 h-12 text-sm focus-visible:ring-[#FF6B35]"
          />
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
          <button data-testid="cat-all"
            onClick={() => setParam("category", "all")}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold transition-all ${
              category === "all" ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/5 hover:bg-[#FAF1E1]"
            }`}>
            Tout
          </button>
          {CATEGORIES.map(c => {
            const Icon = Icons[c.icon] || Icons.Sparkles;
            const active = category === c.id;
            return (
              <button key={c.id} data-testid={`explore-cat-${c.id}`}
                onClick={() => setParam("category", c.id)}
                className={`shrink-0 flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                  active ? "text-white shadow-md" : "bg-white border border-black/5 hover:bg-[#FAF1E1]"
                }`}
                style={active ? { background: c.color, boxShadow: `0 4px 14px ${c.color}40` } : {}}
              >
                <Icon size={15}/>{c.label}
              </button>
            );
          })}
        </div>

        {/* Subcategory chips */}
        {category !== "all" && subList.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3 pb-2">
            <button data-testid="sub-all"
              onClick={() => setParam("subcategory", "all")}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold transition-all ${
                subcategory === "all" ? "bg-[#1A1A1A] text-white" : "bg-[#FAF1E1] text-[#1A1A1A]/70"
              }`}>Toutes</button>
            {subList.map(s => (
              <button key={s.id} data-testid={`sub-${s.id}`}
                onClick={() => setParam("subcategory", s.id)}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold transition-all ${
                  subcategory === s.id ? "bg-[#FF6B35] text-white" : "bg-[#FAF1E1] text-[#1A1A1A]/70 hover:bg-[#F4E1C5]"
                }`}>{s.label}</button>
            ))}
          </div>
        )}

        {/* City chips */}
        {cities.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3 pb-2">
            <button onClick={() => setParam("city", "all")}
              data-testid="city-all"
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-all ${
                city === "all" ? "bg-[#00A6B3] text-white" : "bg-white border border-black/5 text-[#1A1A1A]/70"
              }`}>Toute l'île</button>
            {cities.map(ct => (
              <button key={ct} data-testid={`city-${ct}`}
                onClick={() => setParam("city", ct)}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-all ${
                  city === ct ? "bg-[#00A6B3] text-white" : "bg-white border border-black/5 text-[#1A1A1A]/70 hover:bg-[#FAF1E1]"
                }`}>{ct}</button>
            ))}
          </div>
        )}

        {/* Results */}
        <div className="mt-8" data-testid="explore-results">
          <div className="text-sm text-[#1A1A1A]/60 mb-4">
            {loading ? "Chargement…" : `${places.length} résultat${places.length > 1 ? "s" : ""}`}
          </div>
          {!loading && places.length === 0 && (
            <div className="text-center py-16 text-[#1A1A1A]/50">Aucun résultat. Essayez d'élargir vos filtres.</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {places.map((p, i) => (
              <PlaceCard key={p.id} place={p} index={i}/>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
